import React, { useState, useRef, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, ButtonGroup, Text, CircularProgress, Center,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, useToast
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import {faker} from '@faker-js/faker';
import ReCAPTCHA from 'react-google-recaptcha';
import { withNamespaces } from 'react-i18next';
import { ERROR_CODES } from "../../../constants";
import { displayConfirmAlert } from "../../utils"

function ForgotPasswordModal({ 
    initialRef, isOpen, onClose, t
}) {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false)
    const [isHuman, setIsHuman] = useState(false);
    const toast = useToast();
    const captchaRef = useRef(null);

    const handleChangeCaptcha = async(value) => {
        try
        {
            let response = await axios.post(`${process.env.REACT_APP_WEB_SERVER_URL}/authenticateReCaptcha`, {token: value}, {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            });

            if(response.data && response.data.action === "Human") {
                setIsHuman(true);
            } else {
                setIsHuman(false);
            }
        }
        catch(error) {
            if(error?.code) {
                toast({
                    description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }
    }

    const validate = (values) => {
        const errors = {};

        if(!values.phone) {
            errors.phone = "Required";
        }
        if(!values.email) {
            errors.email = "Required";
        }
        
        return errors;
    }

    const onSubmit = async(values) => {
        setIsLoading(true);
        try
        {
            const response = await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/forgot_password`, values, {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            });
            
            if(response.data?.status === 200) {
                const { id, full_name, email } = response.data?.data
                const forgotPasswordCode = faker.string.alphanumeric(16);

                await axios.put(`${process.env.REACT_APP_API_ENDPOINT_URL}/users/${id}`, {
                    password_reset: forgotPasswordCode
                }, {
                    auth: {
                        username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                        password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                    }
                });

                const forgotPasswordEmailRes = await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/emails/forgot-password`, {
                    name: full_name,
                    email,
                    provideNewPasswordUrl: `${process.env.REACT_APP_URL}/forgot_password/${forgotPasswordCode}`
                }, {
                    auth: {
                        username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                        password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                    }
                })

                if(forgotPasswordEmailRes.data?.status === 200) {
                    onClose();
                    displayConfirmAlert("Hệ thống đã gửi thông tin cấp mật khẩu mới vào email của bạn!")
                } else {
                    if(!toast.isActive("forgot-password-toast")) {
                        toast({
                            id: "forgot-password-toast",
                            title: `Lỗi`,
                            description: "Hệ thống không gửi được email! Vui lòng thử lại!",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                            position: "bottom-right"
                        })
                    }
                }
            } else if(response.data?.status === 0) {
                if(!toast.isActive("forgot-password-toast")) {
                    toast({
                        id: "forgot-password-toast",
                        title: `Lỗi`,
                        description: response.data?.msg,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                        position: "bottom-right"
                    })
                }
            }
        }
        catch(error) {
            if(error?.code) {
                toast({
                    description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }
        setIsLoading(false)
    }

    return (
        <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader textTransform="uppercase">
                    {t("Forgot_Password")}
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={2}>
                    <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        render={({ handleSubmit, form, submitting, pristine, valid, values }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Flex direction="column">
                                        <Field name="phone">
                                            {({ input, meta }) => (
                                                <FormControl isRequired>
                                                    <FormLabel>{t("Phone")}</FormLabel>
                                                    <Input {...input} type='tel' placeholder={t("Type_Phone")} />
                                                </FormControl>
                                            )}
                                        </Field>

                                        <Field name="email">
                                            {({ input, meta }) => (
                                                <FormControl isRequired>
                                                    <FormLabel>{t("Email")}</FormLabel>
                                                    <Input {...input} type='email' placeholder={t("Type_Email")} />
                                                </FormControl>
                                            )}
                                        </Field>

                                        <Center mt={2}>
                                            <ReCAPTCHA 
                                                sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                                                onChange={handleChangeCaptcha}
                                                ref={captchaRef}
                                            />
                                        </Center>

                                        <Button my={4} colorScheme='green' width="full" type='submit' isDisabled={!valid || !isHuman}>
                                            {
                                                isLoading ? (
                                                    <CircularProgress isIndeterminate size="24px" color='orange'/>
                                                ) : (
                                                    t("Provide_New_Password")
                                                )
                                            }
                                        </Button>
                                    </Flex>
                                </form>
                            )
                        }}
                    />
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

export default withNamespaces()(ForgotPasswordModal);